import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  BiDashboardPermission,
  BiDashboardSummary,
  BiSubjectRole,
} from '@euler/model/bi/dashboard';
import { Check } from 'lucide-react-native';
import { memo } from 'react';
import { Pressable, View } from 'react-native';
import { BiTag } from '../../components/ui/BiTag';
import { biIsStaffDashboard } from '../../functions/biIsStaffDashboard';

const kRoleTags: Record<BiSubjectRole, { text: string; color: string }> = {
  [BiSubjectRole.Advisor]: { text: '服务顾问', color: aiColors.primary },
  [BiSubjectRole.Technician]: { text: '技师', color: aiColors.green },
};

/**
 * One dashboard in the header picker: a staff dashboard tagged with its
 * subject's role; a set-as-default action on every one but the default.
 */
export const PickerRow = memo(
  ({
    item,
    current,
    setting,
    onOpen,
    onSetDefault,
  }: {
    item: BiDashboardSummary;
    current: boolean;
    /** Its 设为默认 is in flight. */
    setting: boolean;
    onOpen: () => void;
    onSetDefault: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const shared = item.permission !== BiDashboardPermission.Manage;
    const role =
      biIsStaffDashboard(item) && item.subjectRole
        ? kRoleTags[item.subjectRole]
        : null;
    return (
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Pressable
          accessibilityRole="menuitem"
          accessibilityLabel={item.name}
          accessibilityState={{ selected: current }}
          onPress={onOpen}
          style={({ pressed }) => ({
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            gap: rpx(8),
            paddingVertical: rpx(12),
            paddingHorizontal: rpx(4),
            opacity: pressed ? 0.6 : 1,
          })}
        >
          <View style={{ width: 16 }}>
            {current ? <Check size={16} color={aiColors.primary} /> : null}
          </View>
          <Typography.Text
            size={rpx(14)}
            color={current ? aiColors.primary : aiColors.foreground}
            numberOfLines={1}
            style={{ flexShrink: 1 }}
          >
            {item.name}
          </Typography.Text>
          {role ? <BiTag text={role.text} color={role.color} /> : null}
          {item.isDefault ? (
            <BiTag text="默认" color={aiColors.primary} />
          ) : null}
          {shared && item.ownerName ? (
            <Typography.Text
              size={rpx(12)}
              color={aiColors.mutedForeground}
              numberOfLines={1}
            >
              {item.ownerName} 共享
            </Typography.Text>
          ) : null}
        </Pressable>
        {item.isDefault ? null : (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`把「${item.name}」设为默认`}
            accessibilityState={{ busy: setting }}
            disabled={setting}
            onPress={onSetDefault}
            hitSlop={6}
            style={({ pressed }) => ({
              minHeight: 44,
              justifyContent: 'center',
              paddingHorizontal: rpx(8),
              opacity: setting ? 0.4 : pressed ? 0.6 : 1,
            })}
          >
            <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
              设为默认
            </Typography.Text>
          </Pressable>
        )}
      </View>
    );
  },
);
